import React from 'react';
import { useDispatch } from 'react-redux';
import type {
  Task,
  TaskLink,
  TaskPriority,
  TaskStatus,
} from '../../api/tasks/interface';
import { useSelector } from '../store';
import { type TasksState, actions as tasksActions } from './logic';

export interface CreateTaskInput {
  title: string;
  due?: string | null;
  priority?: TaskPriority;
  assignee?: string | null;
  link?: TaskLink | null;
  source?: string;
  description?: string | null;
  tags?: string[];
}

export interface UpdateTaskInput {
  title?: string;
  due?: string | null;
  priority?: TaskPriority;
  assignee?: string | null;
  status?: TaskStatus;
  link?: TaskLink | null;
  description?: string | null;
  tags?: string[];
}

interface TasksHookState {
  tasks: Task[];
  // Whether this dossier's tasks are in the slice — drives the mount-time fetch
  // guard (matches the files slice's convention).
  hasData: boolean;
  // True while a tasks fetch is pending and nothing is loaded yet, so the panel
  // can show a spinner instead of a flash of "no tasks".
  loading: boolean;
}

export interface TasksHookHandler {
  fetchTasks: () => void;
  createTask: (input: CreateTaskInput) => void;
  updateTask: (taskId: string, changes: UpdateTaskInput) => void;
  deleteTask: (taskId: string) => void;
  addTask: (task: Task) => void;
}

const EMPTY: Task[] = [];

export default function useTasksHook(
  dossierId: string
): [TasksHookState, TasksHookHandler] {
  const dispatch = useDispatch();
  const tasks = useSelector((s) => s.tasks.tasks[dossierId] ?? EMPTY);
  const hasData = useSelector((s) => s.tasks.tasks[dossierId] !== undefined);
  const fetchPending = useSelector(
    (s) => s.tasks.actions.fetchTasks.status === 'pending'
  );
  const loading = fetchPending && !hasData;

  const handler = React.useMemo<TasksHookHandler>(
    () => ({
      fetchTasks: () => {
        dispatch(tasksActions.fetchTasks(dossierId));
      },
      createTask: (input: CreateTaskInput) => {
        dispatch(tasksActions.createTask({ dossierId, ...input }));
      },
      updateTask: (taskId: string, changes: UpdateTaskInput) => {
        dispatch(tasksActions.updateTaskOptimistic({ taskId, changes }));
        dispatch(tasksActions.updateTask({ dossierId, taskId, ...changes }));
      },
      deleteTask: (taskId: string) => {
        dispatch(tasksActions.deleteTask({ dossierId, taskId }));
      },
      addTask: (task: Task) => {
        dispatch(tasksActions.taskAdded({ dossierId, task }));
      },
    }),
    [dispatch, dossierId]
  );

  return [{ tasks, hasData, loading }, handler];
}

interface TaskHookState {
  task: Task | null;
  taskActions: TasksState['actions'];
}

interface TaskHookHandler {
  fetchTask: () => void;
  fetchAllTags: () => void;
  enqueueTagOp: (op: { kind: 'add' | 'remove'; tag: string }) => void;
  updateTask: (changes: UpdateTaskInput) => void;
  respondToTask: (arg: {
    intent: 'resolve' | 'defer';
    description: string;
    defer_to?: string;
  }) => void;
  commentOnTask: (arg: {
    text: string;
    mentions?: string[];
    scope?: 'comment' | 'claim';
  }) => void;
  deleteTask: () => void;
}

// Single-task data-access hook: returns the cached task (via byId), the slice's
// `actions` map (so consumers can read statuses for spinners), and dispatchers.
export function useTaskHook(
  dossierId: string,
  taskId: string
): [TaskHookState, TaskHookHandler] {
  const dispatch = useDispatch();
  const task = useSelector((s) => s.tasks.byId[taskId] ?? null);
  const taskActions = useSelector((s) => s.tasks.actions);

  const handler = React.useMemo<TaskHookHandler>(
    () => ({
      fetchTask: () => {
        dispatch(tasksActions.fetchTask(taskId));
      },
      fetchAllTags: () => {
        dispatch(tasksActions.fetchAllTags());
      },
      enqueueTagOp: (op: { kind: 'add' | 'remove'; tag: string }) => {
        dispatch(tasksActions.enqueueTagOp({ taskId, ...op }));
      },
      updateTask: (changes: UpdateTaskInput) => {
        dispatch(tasksActions.updateTaskOptimistic({ taskId, changes }));
        dispatch(tasksActions.updateTask({ dossierId, taskId, ...changes }));
      },
      respondToTask: (arg) => {
        dispatch(tasksActions.respondToTask({ taskId, ...arg }));
      },
      commentOnTask: (arg) => {
        dispatch(tasksActions.commentOnTask({ taskId, ...arg }));
      },
      deleteTask: () => {
        dispatch(tasksActions.deleteTask({ dossierId, taskId }));
      },
    }),
    [dispatch, dossierId, taskId]
  );

  return [{ task, taskActions }, handler];
}
